import React, { useState, useEffect } from 'react';
import { X, Home, MapPin, DollarSign, Calendar } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';
import type { Client, FormData } from '../types';
import NeighborhoodInsights from './NeighborhoodInsights';

interface SellerPropertiesModalProps {
  seller: Client;
  onClose: () => void;
}

const SellerPropertiesModal: React.FC<SellerPropertiesModalProps> = ({ seller, onClose }) => {
  const [properties, setProperties] = useState<FormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [propertyFormIds, setPropertyFormIds] = useState<string[]>([]);

  useEffect(() => {
    loadSellerProperties();
  }, [seller]);

  const loadSellerProperties = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);

      const idToken = await auth.currentUser.getIdToken();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/get-seller-properties?sellerId=${seller.id}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch seller properties');
      }

      const data = await response.json();
      const formIds = data.properties.map((p: any) => p.form_id);
      setPropertyFormIds(formIds);

      if (formIds.length > 0) {
        const propertiesData: FormData[] = [];
        for (const formId of formIds) {
          const formQuery = query(
            collection(db, 'forms'),
            where('__name__', '==', formId)
          );
          const formSnapshot = await getDocs(formQuery);

          if (!formSnapshot.empty) {
            const doc = formSnapshot.docs[0];
            const docData = doc.data();
            propertiesData.push({
              formId: doc.id,
              userId: docData.userId,
              createdAt: docData.createdAt || docData.timestamp || new Date().toISOString(),
              reportId: docData.reportId,
              generatedAt: docData.generatedAt,
              lastModified: docData.lastModified,
              version: docData.version || 1,
              status: docData.status || 'completed',
              address: docData.address || {},
              property: docData.property || {},
              imageUrls: docData.imageUrls || { propertyImages: [] },
              nearbyPlaces: docData.nearbyPlaces || {}
            } as FormData);
          }
        }
        setProperties(propertiesData);
      }
    } catch (error) {
      console.error('Error loading seller properties:', error);
      toast.error('שגיאה בטעינת נכסי המוכר');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]" dir="rtl">
      <div className="bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-orange-600 to-orange-700">
          <div className="flex items-center gap-3">
            <Home size={28} className="text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">נכסים של {seller.name}</h2>
              <p className="text-orange-100 text-sm">מוכר נכס</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                <p className="text-gray-400">טוען נכסים...</p>
              </div>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-12">
              <Home size={64} className="mx-auto text-gray-600 mb-4" />
              <p className="text-xl text-gray-400 mb-2">אין נכסים משוייכים למוכר זה</p>
              <p className="text-gray-500">ניתן לשייך נכסים למוכר מרשימת הנכסים</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {properties.map(property => (
                <div
                  key={property.formId}
                  className="bg-gray-750 border border-gray-700 rounded-lg p-5 hover:border-orange-500 transition-all"
                >
                  <div className="flex items-start gap-4 mb-4">
                    {property.imageUrls.propertyImages && property.imageUrls.propertyImages.length > 0 && (
                      <img
                        src={property.imageUrls.propertyImages[0]}
                        alt="תמונת נכס"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-start gap-2 mb-2">
                        <MapPin size={18} className="text-orange-400 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="text-lg font-bold text-white">
                            {property.address.city}
                          </h3>
                          <p className="text-gray-300 text-sm">
                            {property.address.street} {property.address.houseNumber}
                          </p>
                          {property.address.neighborhood && (
                            <p className="text-gray-400 text-xs">{property.address.neighborhood}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                    <div className="bg-gray-700 rounded px-3 py-2">
                      <span className="text-gray-400">מחיר: </span>
                      <span className="text-white font-medium">
                        ₪{property.property.price?.toLocaleString()}
                      </span>
                    </div>
                    <div className="bg-gray-700 rounded px-3 py-2">
                      <span className="text-gray-400">חדרים: </span>
                      <span className="text-white font-medium">
                        {property.property.rooms}
                      </span>
                    </div>
                    {property.property.size && (
                      <div className="bg-gray-700 rounded px-3 py-2">
                        <span className="text-gray-400">שטח: </span>
                        <span className="text-white font-medium">
                          {property.property.size} מ"ר
                        </span>
                      </div>
                    )}
                    {property.property.floor !== undefined && (
                      <div className="bg-gray-700 rounded px-3 py-2">
                        <span className="text-gray-400">קומה: </span>
                        <span className="text-white font-medium">
                          {property.property.floor}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar size={14} />
                      <span>נוצר: {new Date(property.createdAt).toLocaleDateString('he-IL')}</span>
                    </div>
                  </div>

                  <NeighborhoodInsights
                    city={property.address.city}
                    neighborhood={property.address.neighborhood}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-700 bg-gray-750">
          <div className="flex justify-between items-center">
            <p className="text-gray-400">סה"כ נכסים: {properties.length}</p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
            >
              סגור
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerPropertiesModal;
